import { todayIn, type IanaZone, type LocalDate } from '@shared/calendar';
import { DEFAULT_DURATION_SECONDS, DEFAULT_START_SECOND, MAX_DESCRIPTION_LENGTH } from '@shared/entry-rules';
import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useEntryDefaults, useSaveEntry } from '@/hooks/use-entries';
import { useMe } from '@/hooks/use-me';
import { ApiClientError } from '@/lib/api';
import { formatShortLocalDate } from '@/lib/format';
import { GENERIC_ERROR_MESSAGE, handleGlobalError, NETWORK_ERROR_MESSAGE } from '@/lib/query-client';
import type { EntryBody, EntryListItem } from '@/types/entries';
import { DescriptionAutocomplete } from './DescriptionAutocomplete';
import { DurationInput } from './DurationInput';
import {
  deriveEnd,
  durationFromEnd,
  endDayMarker,
  hasErrors,
  isValidEntryDate,
  savedDates,
  secondsToTimeInput,
  timeInputToSeconds,
  validateEntryForm,
  type EntryFormErrors,
} from './entry-form-model';
import { ProjectPicker, useManualEntryChoices } from './ProjectPicker';
import { useJoinAnyProject } from '@/hooks/use-project-members';

const SECONDS_PER_MINUTE = 60;
const HTTP_BAD_REQUEST = 400;
const HTTP_NOT_FOUND = 404;
const HTTP_UNPROCESSABLE = 422;
const EDIT_GONE_MESSAGE = 'This entry no longer exists. Close and refresh the day.';
const CREATE_PROJECT_GONE_MESSAGE = 'The selected project no longer exists. Choose another.';
// These codes need the global handler (redirect to /login) even though the mutation is silent.
const GLOBAL_ONLY_CODES = new Set(['ACCOUNT_DEACTIVATED', 'SESSION_EXPIRED']);

type FieldName = keyof EntryFormErrors;

export interface EntryFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  entry?: EntryListItem;
  initialDate?: LocalDate;
  /** Project detail's project: overrides the default project in create mode. */
  contextProjectId?: string;
}

interface FormState {
  projectId: string | null;
  description: string;
  date: string;
  startSecond: number | null;
  durationSeconds: number | null;
}

function emptyState(date: LocalDate): FormState {
  return { projectId: null, description: '', date, startSecond: null, durationSeconds: null };
}

function stateFromEntry(entry: EntryListItem): FormState {
  return {
    projectId: entry.projectId,
    description: entry.description ?? '',
    date: entry.date,
    startSecond: entry.startSecond,
    durationSeconds: entry.durationSeconds,
  };
}

export const MANUAL_OFF_MESSAGE = 'Manual entries are turned off for your projects.';

function saveErrorMessage(error: unknown, mode: 'create' | 'edit'): string {
  if (error instanceof ApiClientError) {
    if (error.statusCode === HTTP_NOT_FOUND) return mode === 'edit' ? EDIT_GONE_MESSAGE : CREATE_PROJECT_GONE_MESSAGE;
    if (error.statusCode === HTTP_BAD_REQUEST || error.statusCode === HTTP_UNPROCESSABLE) return error.message;
    return GENERIC_ERROR_MESSAGE;
  }
  if (error instanceof TypeError) return NETWORK_ERROR_MESSAGE;
  return GENERIC_ERROR_MESSAGE;
}

function FieldError({ id, message }: { id: string; message: string | undefined }): JSX.Element | null {
  if (!message) return null;
  return (
    <p id={id} className="text-xs text-danger-fg">
      {message}
    </p>
  );
}

/** Add or edit one time entry (architecture sprint-03 §9.3). Errors stay inline; the dialog stays open. */
export function EntryFormDialog(props: EntryFormDialogProps): JSX.Element {
  const { open, onOpenChange, mode } = props;
  const { data: me } = useMe();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Add entry' : 'Edit entry'}</DialogTitle>
          <DialogDescription className="text-xs">{me ? `Times are in your timezone (${me.timezone}).` : 'Times are in your timezone.'}</DialogDescription>
        </DialogHeader>
        {open && me ? <EntryForm {...props} tz={me.timezone} /> : <Skeleton className="h-64 w-full" />}
      </DialogContent>
    </Dialog>
  );
}

function EntryForm({ onOpenChange, mode, entry, initialDate, contextProjectId, tz }: EntryFormDialogProps & { tz: IanaZone }): JSX.Element {
  const save = useSaveEntry();
  const [state, setState] = useState<FormState>(() =>
    mode === 'edit' && entry ? stateFromEntry(entry) : emptyState(initialDate ?? todayIn(tz)),
  );
  const [initialised, setInitialised] = useState(mode === 'edit');
  const [startTouched, setStartTouched] = useState(mode === 'edit');
  const [shown, setShown] = useState<Set<FieldName>>(new Set());
  const [formError, setFormError] = useState<string | null>(null);
  // Sprint 11 (F4): an Admin refused with PROJECT_NOT_MEMBER may join and re-submit once.
  const { data: me } = useMe();
  const join = useJoinAnyProject();
  const [joinFor, setJoinFor] = useState<{ projectId: string; body: EntryBody } | null>(null);
  const manual = useManualEntryChoices(true);

  // Create mode: defaults follow the Date until Start is touched (§9.3).
  const defaultsDate = isValidEntryDate(state.date) ? state.date : (initialDate ?? todayIn(tz));
  const defaults = useEntryDefaults(defaultsDate, mode === 'create' && !startTouched);

  useEffect(() => {
    if (mode !== 'create' || startTouched) return;
    if (defaults.data && defaults.data.date === defaultsDate) {
      const data = defaults.data;
      if (!initialised) {
        setState((s) => ({
          ...s,
          startSecond: data.startSecond,
          durationSeconds: data.durationSeconds,
          projectId: contextProjectId ?? data.projectId,
        }));
        setInitialised(true);
      } else {
        setState((s) => ({ ...s, startSecond: data.startSecond }));
      }
    } else if (defaults.isError && !initialised) {
      // The global toast already reported it; fall back to the documented defaults.
      setState((s) => ({ ...s, startSecond: DEFAULT_START_SECOND, durationSeconds: DEFAULT_DURATION_SECONDS, projectId: contextProjectId ?? null }));
      setInitialised(true);
    }
  }, [mode, startTouched, defaults.data, defaults.isError, defaultsDate, initialised, contextProjectId]);

  if (!initialised) return <Skeleton className="h-64 w-full" />;

  const errors = validateEntryForm(state);
  const visible = (field: FieldName): string | undefined => (shown.has(field) ? errors[field] : undefined);
  const reveal = (field: FieldName): void => setShown((prev) => new Set(prev).add(field));

  const spanValid = isValidEntryDate(state.date) && state.startSecond !== null && state.durationSeconds !== null && state.durationSeconds > 0;
  const end = spanValid ? deriveEnd(state.date, state.startSecond ?? 0, state.durationSeconds ?? 0, tz) : null;
  const dates = spanValid && !errors.durationSeconds ? savedDates(state.date, state.startSecond ?? 0, state.durationSeconds ?? 0, tz) : [];
  const marker = end ? endDayMarker(end.dayOffset) : '';

  function update(patch: Partial<FormState>): void {
    setState((s) => ({ ...s, ...patch }));
    setFormError(null);
  }

  function onStartChange(value: string): void {
    const parsed = timeInputToSeconds(value);
    setStartTouched(true);
    if (parsed === null) return update({ startSecond: null });
    // Keep stored seconds (a timer entry) unless the shown minute actually changed.
    const current = state.startSecond;
    if (current !== null && Math.floor(current / SECONDS_PER_MINUTE) === Math.floor(parsed / SECONDS_PER_MINUTE)) return;
    update({ startSecond: parsed });
  }

  function onEndChange(value: string): void {
    const endSecond = timeInputToSeconds(value);
    if (endSecond === null || state.startSecond === null || !isValidEntryDate(state.date)) return;
    update({ durationSeconds: durationFromEnd(state.date, state.startSecond, endSecond, tz) });
  }

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    setShown(new Set<FieldName>(['date', 'startSecond', 'durationSeconds', 'description']));
    if (hasErrors(errors) || state.startSecond === null || state.durationSeconds === null) return;
    const body = {
      projectId: state.projectId,
      description: state.description.trim() || null,
      date: state.date,
      startSecond: state.startSecond,
      durationSeconds: state.durationSeconds,
    };
    setFormError(null);
    setJoinFor(null);
    submitBody(body, true);
  }

  function submitBody(body: EntryBody, allowJoin: boolean): void {
    save.mutate(
      { id: mode === 'edit' ? entry?.id : undefined, body },
      {
        onSuccess: (result) => {
          const count = result.items.length;
          toast.success(count > 1 ? `Saved as ${count} entries (crosses midnight).` : 'Entry saved.');
          onOpenChange(false);
        },
        onError: (error) => {
          if (error instanceof ApiClientError && GLOBAL_ONLY_CODES.has(error.code)) {
            handleGlobalError(error, undefined);
            return;
          }
          if (allowJoin && error instanceof ApiClientError && error.code === 'PROJECT_NOT_MEMBER' && me?.role === 'admin' && body.projectId) {
            setJoinFor({ projectId: body.projectId, body });
          }
          setFormError(saveErrorMessage(error, mode));
        },
      },
    );
  }

  function joinAndResubmit(): void {
    if (!joinFor) return;
    const pending = joinFor;
    join.mutate(pending.projectId, {
      onSuccess: () => {
        setJoinFor(null);
        setFormError(null);
        submitBody(pending.body, false);
      },
    });
  }

  const describedBy = (field: FieldName): string | undefined => (visible(field) ? `entry-${field}-error` : undefined);

  return (
    <form onSubmit={onSubmit} className="grid gap-3" noValidate>
      <div className="grid gap-1.5">
        <Label id="entry-project-label" htmlFor="entry-project">
          Project
        </Label>
        <ProjectPicker
          id="entry-project"
          aria-labelledby="entry-project-label entry-project-value"
          value={state.projectId}
          onChange={(projectId) => update({ projectId })}
          currentProject={mode === 'edit' ? (entry?.project ?? null) : null}
          manualOnly
        />
        {mode === 'create' && !manual.anyAllowed && <p className="text-xs text-danger-fg">{MANUAL_OFF_MESSAGE}</p>}
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="entry-description">Description (optional)</Label>
        <DescriptionAutocomplete
          id="entry-description"
          value={state.description}
          onChange={(description) => update({ description })}
          onBlur={() => reveal('description')}
          maxLength={MAX_DESCRIPTION_LENGTH}
          placeholder="What did you work on?"
          aria-describedby={describedBy('description')}
          aria-invalid={Boolean(visible('description'))}
        />
        <FieldError id="entry-description-error" message={visible('description')} />
      </div>

      <div className="grid gap-1.5">
        <Label htmlFor="entry-date">Date</Label>
        <Input
          id="entry-date"
          type="date"
          value={state.date}
          onChange={(e) => update({ date: e.target.value })}
          onBlur={() => reveal('date')}
          aria-describedby={describedBy('date')}
          aria-invalid={Boolean(visible('date'))}
        />
        <FieldError id="entry-date-error" message={visible('date')} />
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div className="grid content-start gap-1.5">
          <Label htmlFor="entry-start">Start</Label>
          <Input
            id="entry-start"
            type="time"
            step={60}
            value={state.startSecond === null ? '' : secondsToTimeInput(state.startSecond)}
            onChange={(e) => onStartChange(e.target.value)}
            onBlur={() => reveal('startSecond')}
            aria-describedby={describedBy('startSecond')}
            aria-invalid={Boolean(visible('startSecond'))}
          />
          <FieldError id="entry-startSecond-error" message={visible('startSecond')} />
        </div>
        <div className="grid content-start gap-1.5">
          <Label htmlFor="entry-duration">Duration (HH:MM)</Label>
          <DurationInput
            id="entry-duration"
            value={state.durationSeconds}
            onChange={(durationSeconds) => update({ durationSeconds })}
            onBlur={() => reveal('durationSeconds')}
            aria-describedby={describedBy('durationSeconds')}
            aria-invalid={Boolean(visible('durationSeconds'))}
          />
          <FieldError id="entry-durationSeconds-error" message={visible('durationSeconds')} />
        </div>
        <div className="grid content-start gap-1.5">
          <Label htmlFor="entry-end">End</Label>
          <div className="flex items-center gap-2">
            <Input
              id="entry-end"
              type="time"
              step={60}
              value={end ? secondsToTimeInput(end.second) : ''}
              onChange={(e) => onEndChange(e.target.value)}
              aria-describedby={marker ? 'entry-end-marker' : undefined}
            />
            {marker && (
<Badge id="entry-end-marker">{marker}</Badge>
            )}
          </div>
        </div>
      </div>

      {dates.length > 1 && (
        <p className="text-xs text-muted-foreground">
          Crosses midnight: this saves as {dates.length} entries ({dates.map(formatShortLocalDate).join(', ')}).
        </p>
      )}

      {formError && (
        <div role="alert" className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger-fg">
          {formError}
          {joinFor && (
            <div className="mt-2">
              <Button type="button" size="xs" variant="outline" onClick={joinAndResubmit} disabled={join.isPending || save.isPending}>
                {join.isPending ? 'Joining…' : 'Join as Member'}
              </Button>
            </div>
          )}
        </div>
      )}

      <DialogFooter>
        <Button type="submit" className="flex-1" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : mode === 'create' ? 'Add entry' : 'Save changes'}
        </Button>
        <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
      </DialogFooter>
    </form>
  );
}
